import React from 'react';
import { SOURCE_PALETTE } from './constants';
import { SourceIcon } from './SourceIcon';

export interface RevenueSourceSegment {
    name: string;
    revenue: number;
    isPaid: boolean;
    attribution: number;
    color?: string;
    parentName?: string;
    // Status fields for detailed naming
    channelName?: string;
    isDisconnected?: boolean;
    isMatched?: boolean;
    isSelected?: boolean;
}

interface RevenueSourceBreakdownUIProps {
    segments: RevenueSourceSegment[];
    formatCurrency: (val: number, symbol: string) => string;
    currencySymbol: string;
    t: (key: string, options?: any) => string;
    onSegmentClick?: (segment: RevenueSourceSegment) => void;
}

export const RevenueSourceBreakdownUI: React.FC<RevenueSourceBreakdownUIProps> = ({ 
    segments, 
    formatCurrency, 
    currencySymbol, 
    t,
    onSegmentClick
}) => {
    if (!segments || segments.length === 0) return null;

    return (
        <div className="space-y-4">
            {segments.map((source: RevenueSourceSegment, idx: number) => (
                <div 
                    key={`${source.name}-${idx}`} 
                    className="group cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/30 p-1 -m-1 rounded-lg transition-all"
                    onClick={() => onSegmentClick?.(source)}
                >
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 mb-1.5">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                            <SourceIcon 
                                name={source.parentName || source.name} 
                                className="w-3 h-3 shrink-0" 
                                color={source.color || SOURCE_PALETTE[idx % SOURCE_PALETTE.length]}
                            />
                            <span className="text-[10px] font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-tight truncate">
                                {(() => {
                                    const channelName = source.channelName || t('pages.traffic.modals.unknownChannel', { defaultValue: 'Unknown' });
                                    if (source.isDisconnected) {
                                        return `${channelName} ${t('pages.traffic.modals.disconnectedLabel', { defaultValue: 'DISCONNECTED' })}`;
                                    }
                                    if (source.isMatched === false) {
                                        return t('pages.traffic.modals.unknownSourceLabel', { defaultValue: 'UNKNOWN SOURCE' });
                                    }
                                    if (source.isSelected === false) {
                                        return `${source.name} - ${t('pages.traffic.modals.untrackedLabel', { defaultValue: 'UNTRACKED' })}`;
                                    }
                                    return source.name;
                                })()}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-auto">
                            <span className="text-xs font-black text-neutral-900 dark:text-neutral-100 italic">
                                {formatCurrency(source.revenue, currencySymbol)}
                            </span>
                            <span className="text-[10px] font-bold text-accent-primary bg-accent-primary/5 px-1.5 py-0.5 rounded border border-accent-primary/10">
                                {source.attribution.toFixed(1)}%
                            </span>
                        </div>
                    </div>
                    <div className="w-full h-1 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                        <div 
                            className="h-full rounded-full transition-all duration-500" 
                            style={{ 
                                width: `${source.attribution}%`, 
                                backgroundColor: source.color || SOURCE_PALETTE[idx % SOURCE_PALETTE.length] 
                            }} 
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};
